import React, { ReactNode } from 'react';
import { useRBAC, Permission, Role } from '../hooks/useRBAC';

interface PermissionGuardProps {
	children: ReactNode;
	permission?: Permission;
	role?: Role;
	fallback?: ReactNode;
}

/**
 * A component that conditionally renders its children based on user permissions or role
 * @param children - The content to render if the user has permission
 * @param permission - The required permission string (e.g., 'submissions.create')
 * @param role - The minimum required role
 * @param fallback - Optional content to render if permission check fails
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
	children,
	permission,
	role,
	fallback = null,
}) => {
	const { hasPermission, isAtLeastRole, loading } = useRBAC();

	if (loading) {
		return null; // Or a loading indicator
	}

	// Check permission if specified
	if (permission && !hasPermission(permission)) {
		return <>{fallback}</>;
	}

	// Check role if specified
	if (role && !isAtLeastRole(role)) {
		return <>{fallback}</>;
	}

	// If no permission or role is specified, or user has access
	return <>{children}</>;
};

export default PermissionGuard;
